import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Sparkles, BookOpen, ArrowLeftRight, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EducationPage = () => {
  const { t } = useLanguage();
  const edu = t('education') || {};
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Navbar />
      
      <main className="pt-32 pb-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border border-cyan-500/30">
              <BookOpen size={36} className="text-cyan-400" />
            </div>
          </div>

          {/* Badge */}
          <div className="mb-8">
            <span className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-4 py-2">
              <Sparkles size={16} className="text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium">{edu?.badge || 'Coming Soon'}</span>
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            {edu?.title || 'Free Blockchain Education'}
          </h1>

          {/* Description */}
          <p className="text-xl text-gray-400 mb-4 max-w-2xl mx-auto">
            {edu?.subtitle || "I've always had a passion for teaching and sharing knowledge."}
          </p>
          <p className="text-lg text-gray-500 mb-12 max-w-2xl mx-auto">
            {edu?.description || "Blockchain isn't just technology—it's a culture. From Bitcoin's revolutionary ethos to DeFi's endless possibilities, I want to help you truly understand this world, not just trade in it."}
          </p>

          {/* Available Now - P2P Guide */}
          <div className="mb-12">
            <p className="text-cyan-400 text-sm font-medium mb-4">{edu?.availableNow || 'Available Now'}</p>
            <Link 
              to="/education/p2p"
              className="group block max-w-xl mx-auto bg-gradient-to-br from-cyan-900/20 to-gray-900 border border-cyan-500/30 hover:border-cyan-400/50 rounded-2xl p-6 transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="bg-cyan-500/20 p-4 rounded-xl group-hover:bg-cyan-500/30 transition-colors">
                  <ArrowLeftRight size={28} className="text-cyan-400" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-xl font-bold text-white mb-1">{edu?.p2pCard?.title || 'Complete P2P Guide'}</h3>
                  <p className="text-gray-400 text-sm">{edu?.p2pCard?.desc || 'Everything about peer-to-peer exchange: cash, banking, remittances, privacy...'}</p>
                </div>
                <ArrowRight size={24} className="text-cyan-400 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>

          {/* Email signup */}
          {!submitted ? (
            <form onSubmit={handleSubmit} className="max-w-md mx-auto">
              <p className="text-gray-400 text-sm mb-4">{edu?.emailPrompt || 'Be the first to know when we launch more content'}</p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-12 pr-4 py-3 text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none transition-colors"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-700 text-black font-semibold px-6 py-3 rounded-lg transition-colors"
                >
                  {loading ? '...' : (edu?.notifyBtn || 'Notify Me')}
                </button>
              </div>
            </form>
          ) : (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 max-w-md mx-auto">
              <p className="text-green-400 font-medium">{edu?.success?.title || "You're on the list!"}</p>
              <p className="text-gray-400 text-sm mt-1">{edu?.success?.desc || "We'll notify you when we launch."}</p>
            </div>
          )}

          {/* Topics preview */}
          <div className="mt-16 pt-16 border-t border-gray-800">
            <p className="text-gray-500 text-sm mb-6">{edu?.topicsTitle || "Topics we'll cover"}</p>
            <div className="flex flex-wrap justify-center gap-3">
              {(edu?.topics || ['Bitcoin Philosophy', 'DeFi Fundamentals', 'On-Chain Analysis', 'Risk Management', 'Market Psychology', 'Trading Strategies']).map((topic) => (
                <span key={topic} className="bg-gray-900 border border-gray-800 rounded-full px-4 py-2 text-sm text-gray-400">
                  {topic}
                </span>
              ))}
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default EducationPage;
